import React, { useEffect, useState } from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, LineChart, Line } from 'recharts';
import { backupAPI } from '../services/api';
import DataTable from '../components/DataTable';

const PIE_COLORS    = ['#ca8a04','#2563eb','#16a34a','#dc2626','#7c3aed'];
const FAILURE_COLS  = [
  { key: 'customer_name', label: 'Cliente' },
  { key: 'job_name',      label: 'Job' },
  { key: 'backup_type',   label: 'Tipo' },
  { key: 'fail_count',    label: 'N° Fallimenti' },
  { key: 'last_failure',  label: 'Ultimo Fallimento' },
];

export default function AnalyticsPage({ customer }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    backupAPI.getAnalytics(customer)
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [customer]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Caricamento...</div>;
  if (!data)   return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Errore caricamento dati</div>;

  const pieData  = (data.distribution || []).map(d => ({ name: d.backup_type, value: d.count }));
  const barData  = (data.daily || []).map(d => ({
    day:     new Date(d.day).toLocaleDateString('it-IT', { day:'2-digit', month:'2-digit' }),
    OK:      d.ok,
    FAILED:  d.failed,
    WARNING: d.warning,
  }));

  return (
    <div>
      <h2 style={{ margin: '0 0 20px', fontSize: 22, fontWeight: 700, color: '#1e293b' }}>Analytics</h2>

      {/* Trend giornaliero + Pie */}
      <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 20 }}>
        <div style={{ ...s.card, flex: '1 1 340px' }}>
          <div style={s.cardTitle}>🥧 Distribuzione Tipo Backup</div>
          <div style={{ padding: '8px 0 20px', display: 'flex', justifyContent: 'center' }}>
            <PieChart width={300} height={240}>
              <Pie data={pieData} cx={150} cy={110} outerRadius={90}
                dataKey="value"
                label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}
                labelLine={false} fontSize={11}>
                {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </div>
        </div>

        <div style={{ ...s.card, flex: '2 1 480px' }}>
          <div style={s.cardTitle}>📅 Trend Giornaliero — ultimi 30 giorni</div>
          <div style={{ padding: '8px 16px 20px' }}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={barData} margin={{ bottom: 40 }}>
                <XAxis dataKey="day" angle={-35} textAnchor="end" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="OK"      stackId="a" fill="#16a34a" />
                <Bar dataKey="WARNING" stackId="a" fill="#ca8a04" />
                <Bar dataKey="FAILED"  stackId="a" fill="#dc2626" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Tasso successo per cliente */}
      {data.success_rate?.length > 0 && (
        <div style={{ ...s.card, marginBottom: 20 }}>
          <div style={s.cardTitle}>🏆 Tasso di Successo per Cliente — ultimi 30 giorni</div>
          <div style={{ padding: '8px 16px 20px' }}>
            <ResponsiveContainer width="100%" height={Math.max(120, data.success_rate.length * 40)}>
              <BarChart
                data={data.success_rate}
                layout="vertical"
                margin={{ left: 120, right: 40 }}
              >
                <XAxis type="number" domain={[0,100]} unit="%" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="customer_name" tick={{ fontSize: 12 }} width={120} />
                <Tooltip formatter={(v) => [`${v}%`, 'Successo']} />
                <Bar dataKey="success_pct" radius={[0,4,4,0]}>
                  {data.success_rate.map((entry, i) => (
                    <Cell key={i}
                      fill={entry.success_pct === 100 ? '#16a34a' : entry.success_pct >= 80 ? '#ca8a04' : '#dc2626'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Job con più fallimenti */}
      <DataTable
        title={`💀 Job con più Fallimenti — ultimi 30 giorni`}
        columns={FAILURE_COLS}
        rows={data.top_failures}
        emptyMsg="Nessun fallimento negli ultimi 30 giorni ✅"
      />
    </div>
  );
}

const s = {
  card:     { background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0' },
  cardTitle:{ padding: '14px 20px', fontWeight: 700, fontSize: 15, color: '#1e293b', borderBottom: '1px solid #f1f5f9' },
};
